import React from 'react';
import { View } from 'react-native';
import { YStack, Text, H4, XStack } from "tamagui";
import { LinearGradient } from 'expo-linear-gradient';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface ModernChartWrapperProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  isLoading?: boolean;
  loadingComponent?: React.ReactNode;
  summaryData?: Array<{
    label: string;
    value: string | number;
    color: string;
  }>;
  minHeight?: number;
  minWidth?: number;
}

const ModernChartWrapper: React.FC<ModernChartWrapperProps> = ({
  title,
  subtitle,
  children,
  isLoading = false,
  loadingComponent,
  summaryData = [],
  minHeight = 380,
  minWidth = 340
}) => {
  const theme = themeSelector();

  if (isLoading) {
    return (
      <Animatable.View
        animation="slideInUp"
        duration={400}
        delay={100}
        style={[
          styles.modernCard,
          theme ? styles.modernBgLight : styles.modernBgDark,
          {
            minWidth,
            minHeight,
            overflow: 'hidden',
            backgroundColor: theme ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)',
            borderColor: theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
          }
        ]}
      >
        <YStack flex={1} alignItems="center" justifyContent="center">
          {loadingComponent || (
            <Text style={{ color: theme ? '#64748b' : '#94a3b8' }}>
              Loading...
            </Text>
          )}
        </YStack>
      </Animatable.View>
    );
  }

  return (
    <Animatable.View
      animation="slideInUp"
      duration={400}
      delay={100}
      style={[
        styles.modernCard,
        theme ? styles.modernBgLight : styles.modernBgDark,
        {
          minWidth,
          minHeight,
          overflow: 'hidden',
          backgroundColor: theme ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)',
          borderColor: theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
        }
      ]}
    >
      {/* Modern Header with Gradient */}
      <LinearGradient
        colors={theme
          ? ['rgba(59, 130, 246, 0.05)', 'rgba(139, 92, 246, 0.05)']
          : ['rgba(59, 130, 246, 0.15)', 'rgba(139, 92, 246, 0.15)']
        }
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 80,
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24
        }}
      />

      <XStack justifyContent="space-between" alignItems="center"
        style={{
          paddingHorizontal: 24,
          paddingTop: 20,
          paddingBottom: 16,
          borderBottomWidth: 1,
          borderBottomColor: theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
          marginBottom: 20
        }}
      >
        <YStack>
          <H4 style={{
            fontSize: 18,
            fontWeight: '700',
            color: theme ? '#1e293b' : '#f1f5f9',
            marginBottom: 4
          }}>
            {title}
          </H4>
          {subtitle && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              opacity: 0.8
            }}>
              {subtitle}
            </Text>
          )}
        </YStack>
      </XStack>

      <View style={{ paddingHorizontal: 24, flex: 1 }}>
        {children}
      </View>

      {/* Modern Stats Summary */}
      {summaryData.length > 0 && (
        <XStack flexWrap="wrap" gap={12} marginTop={16} marginHorizontal={24} marginBottom={20} justifyContent="space-between">
          {summaryData.slice(0, 3).map((item, index) => (
            <Animatable.View
              key={index}
              animation="fadeInUp"
              delay={200 + (index * 100)}
              style={[
                styles.modernInfoRow,
                {
                  flex: 1,
                  minWidth: '30%',
                  alignItems: 'center',
                  backgroundColor: `${item.color}${theme ? '15' : '25'}`,
                  borderWidth: 1,
                  borderColor: `${item.color}${theme ? '30' : '50'}`,
                  paddingVertical: 12,
                  paddingHorizontal: 8,
                  borderRadius: 8
                }
              ]}
            >
              <YStack alignItems="center" gap={2}>
                <Text style={[
                  styles.modernFieldLabel,
                  {
                    color: item.color,
                    fontSize: 10
                  }
                ]}>
                  {item.label.toUpperCase()}
                </Text>
                <Text style={[
                  styles.modernFieldValue,
                  {
                    fontSize: 14,
                    fontWeight: '700',
                    color: theme ? '#1e293b' : '#f1f5f9'
                  }
                ]}>
                  {item.value}
                </Text>
              </YStack>
            </Animatable.View>
          ))}
        </XStack>
      )}
    </Animatable.View>
  );
};

export default ModernChartWrapper;
